import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const hearthShapes = [
  {
    id: 'rectangle',
    name: 'Rectangle',
    description: 'Standard rectangular hearth',
    pieces: 1,
    svg: `<svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="20" y="20" width="160" height="80" fill="#2d3748" stroke="#4a5568" stroke-width="2"/>
    </svg>`
  },
  {
    id: 't-shape-single',
    name: 'T-Shape (Single Piece)',
    description: 'One piece T-shaped hearth',
    pieces: 1,
    svg: `<svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M 50 20 L 150 20 L 150 60 L 130 60 L 130 100 L 70 100 L 70 60 L 50 60 Z" fill="#2d3748" stroke="#4a5568" stroke-width="2"/>
    </svg>`
  },
  {
    id: 't-shape-two',
    name: 'T-Shape (Two Pieces)',
    description: 'Two pieces joined to form T-shape',
    pieces: 2,
    svg: `<svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="50" y="20" width="100" height="40" fill="#2d3748" stroke="#4a5568" stroke-width="2"/>
      <rect x="70" y="60" width="60" height="40" fill="#4a5568" stroke="#4a5568" stroke-width="2"/>
      <line x1="70" y1="60" x2="130" y2="60" stroke="#e2e8f0" stroke-width="2" stroke-dasharray="4"/>
    </svg>`
  },
  {
    id: 'quadrant',
    name: 'Quadrant',
    description: 'Quarter circle corner hearth',
    pieces: 1,
    svg: `<svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M 20 100 L 20 20 A 80 80 0 0 1 100 100 Z" fill="#2d3748" stroke="#4a5568" stroke-width="2"/>
    </svg>`
  },
  {
    id: 'custom',
    name: 'Custom Shape',
    description: 'Custom dimensions and shape',
    pieces: 'variable', // Can have 1+ pieces
    svg: `<svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="40" y="30" width="120" height="60" fill="none" stroke="#4a5568" stroke-width="2" stroke-dasharray="8"/>
      <text x="100" y="65" text-anchor="middle" fill="#4a5568" font-size="16">Custom</text>
    </svg>`
  }
];

const hearthMaterials = [
  { 
    id: 'honed-black', 
    name: 'Honed Black Granite', 
    finish: 'Honed',
    imageUrl: 'https://customer-assets.emergentagent.com/job_instant-quote-3/artifacts/5006ei11_honed-granite-hearth.jpg'
  },
  { 
    id: 'leathered-steel-grey', 
    name: 'Leathered Steel Grey Granite', 
    finish: 'Leathered',
    imageUrl: 'https://customer-assets.emergentagent.com/job_instant-quote-3/artifacts/sdwjm4gy_steel%20grey%20leathered.jpg'
  },
  { 
    id: 'custom', 
    name: 'Custom Material', 
    finish: 'Custom',
    imageUrl: 'https://customer-assets.emergentagent.com/job_instant-quote-3/artifacts/xdin88kv_Sandstone.jpeg'
  }
];

function PublicHearthQuote() {
  const navigate = useNavigate();
  const { companySlug } = useParams();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);

  React.useEffect(() => {
    fetchCompanyInfo();
  }, [companySlug]);
  
  const fetchCompanyInfo = async () => {
    try {
      const slug = companySlug || 'set-in-stone';
      const response = await axios.get(`${API}/public/company/${slug}`);
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Failed to load company info');
      setCompanyInfo({
        name: 'Hearth & Fireplace Specialists',
        slug: companySlug || 'set-in-stone'
      });
    }
  };

  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    notes: ''
  });

  const [selectedShape, setSelectedShape] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [customMaterial, setCustomMaterial] = useState('');
  const [hasSlips, setHasSlips] = useState(false);
  const [slipHeight, setSlipHeight] = useState('50');
  const [customSlipHeight, setCustomSlipHeight] = useState('');
  const [deliveryOption, setDeliveryOption] = useState('delivery'); // 'delivery' or 'collection'
  
  // T-Shape cut-out dimensions
  const [sameCutouts, setSameCutouts] = useState(true);
  const [leftCutout, setLeftCutout] = useState({ width: '', depth: '' });
  const [rightCutout, setRightCutout] = useState({ width: '', depth: '' });

  const [pieces, setPieces] = useState([{
    name: 'Main Hearth',
    width_mm: '',
    depth_mm: '',
    thickness: '20mm'
  }]);

  const updatePiece = (index, field, value) => {
    const updated = [...pieces];
    updated[index][field] = value;
    setPieces(updated);
  };

  const addPiece = () => {
    setPieces([...pieces, {
      name: `Piece ${pieces.length + 1}`,
      width_mm: '',
      depth_mm: '',
      thickness: '20mm'
    }]);
  };

  const removePiece = (index) => {
    if (pieces.length > 1) {
      setPieces(pieces.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate
      if (!selectedShape) {
        toast.error('Please select a hearth shape');
        setLoading(false);
        return;
      }

      if (!selectedMaterial) {
        toast.error('Please select a material');
        setLoading(false);
        return;
      }

      if (selectedMaterial.id === 'custom' && !customMaterial.trim()) {
        toast.error('Please enter the custom material name');
        setLoading(false);
        return;
      }

      const validPieces = pieces.filter(p => p.width_mm && p.depth_mm);
      if (validPieces.length === 0) {
        toast.error('Please add at least one piece with dimensions');
        setLoading(false);
        return;
      }

      // Validate T-shape cut-outs
      if (selectedShape.id === 't-shape-single') {
        if (!leftCutout.width || !leftCutout.depth) {
          toast.error('Please enter left cut-out dimensions');
          setLoading(false);
          return;
        }
        if (!sameCutouts && (!rightCutout.width || !rightCutout.depth)) {
          toast.error('Please enter right cut-out dimensions');
          setLoading(false);
          return;
        }
      }

      // Calculate slip height
      const finalSlipHeight = slipHeight === 'custom' ? parseFloat(customSlipHeight) : parseFloat(slipHeight);
      
      // Get material name
      const materialName = selectedMaterial.id === 'custom' ? customMaterial : selectedMaterial.name;

      // Build cut-out info for T-shape
      let cutoutInfo = '';
      if (selectedShape.id === 't-shape-single') {
        const leftInfo = `Left cut-out: ${leftCutout.width}mm x ${leftCutout.depth}mm`;
        const rightInfo = sameCutouts 
          ? 'Right cut-out: Same as left' 
          : `Right cut-out: ${rightCutout.width}mm x ${rightCutout.depth}mm`;
        cutoutInfo = `\n\n*** CUTOUTS ***\n${leftInfo}\n${rightInfo}\n`;
      }

      const payload = {
        ...formData,
        order_type: 'Fireplace/Hearth',
        hearth_shape: selectedShape.name,
        hearth_shape_id: selectedShape.id,
        pieces: validPieces.map(p => ({
          name: p.name,
          width_mm: parseFloat(p.width_mm),
          height_mm: parseFloat(p.depth_mm), // depth becomes height in backend
          thickness: p.thickness,
          material: materialName,
          edge_profile: 'Polished',
          edges: { top: true, right: true, bottom: true, left: true } // All edges polished for hearths
        })),
        has_slips: hasSlips,
        slip_height: hasSlips ? finalSlipHeight : 0,
        stone_colour: materialName,
        supplier: 'Granite',
        colour_name: materialName,
        pm2_cost: 0,
        service_options: { 
          include_templating: false, 
          include_fitting: false,
          delivery_only: deliveryOption === 'delivery',
          collection: deliveryOption === 'collection'
        },
        extras: {},
        quote_type: 'hearth',
        delivery_method: deliveryOption,
        notes: formData.notes + cutoutInfo + `\nDelivery Method: ${deliveryOption === 'delivery' ? 'Delivery' : 'Customer Collection'}`,
        t_shape_cutouts: selectedShape.id === 't-shape-single' ? {
          left: { width: parseFloat(leftCutout.width), depth: parseFloat(leftCutout.depth) },
          right: sameCutouts 
            ? { width: parseFloat(leftCutout.width), depth: parseFloat(leftCutout.depth) }
            : { width: parseFloat(rightCutout.width), depth: parseFloat(rightCutout.depth) }
        } : null,
        company_slug: companySlug || 'set-in-stone'
      };

      await axios.post(`${API}/worktops/public-quote-request`, payload);
      
      setSubmitted(true);
      toast.success('Hearth quote request submitted successfully! We will contact you soon.');
    } catch (error) {
      console.error('Quote request error:', error);
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to submit quote request';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(err => err.msg || JSON.stringify(err)).join(', ');
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-slate-800 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-xl">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h2>
              <p className="text-lg text-gray-600 mb-2">
                Your hearth quote request has been received successfully.
              </p>
              <p className="text-gray-600 mb-8">
                We'll review your requirements and get back to you within 24 hours with a detailed quote.
              </p>
              <Button 
                onClick={() => window.location.reload()} 
                className="bg-gray-800 hover:bg-gray-900"
              >
                Submit Another Request
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-slate-800 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="outline"
          onClick={() => navigate('/')}
          className="mb-6 bg-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        <Card className="shadow-2xl mb-6 border-gray-700">
          <CardHeader className="bg-gradient-to-r from-gray-800 to-slate-700 text-white">
            {companyInfo?.branding?.logo_url || companyInfo?.branding?.logo_data ? (
              <img 
                src={companyInfo.branding.logo_url || companyInfo.branding.logo_data}
                alt={companyInfo?.name || 'Company Logo'}
                className="h-12 mb-3 object-contain"
              />
            ) : null}
            <CardTitle className="text-3xl">
              {companyInfo?.name ? `${companyInfo.name}` : 'Fireplace Hearth Quote Request'}
            </CardTitle>
            <p className="text-gray-300 mt-2">
              {companyInfo?.branding?.company_tagline || 'Custom granite hearths for your fireplace'}
            </p>
            {companyInfo?.phone && (
              <p className="text-gray-400 mt-1 text-sm">
                Call us: <a href={`tel:${companyInfo.phone}`} className="text-blue-300 hover:underline">{companyInfo.phone}</a>
              </p>
            )}
          </CardHeader>
        </Card>

        <form onSubmit={handleSubmit}>
          {/* Contact Details */}
          <Card className="mb-6 shadow-lg">
            <CardHeader className="bg-gray-50">
              <CardTitle className="text-xl">Your Details</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    required
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    required
                    placeholder="john@example.com"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    value={formData.client_phone}
                    onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                    required
                    placeholder="07123 456789"
                  />
                </div>
                <div>
                  <Label>Address *</Label>
                  <Input
                    value={formData.client_address}
                    onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                    required
                    placeholder="Your full address"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Shape Selection */}
          <Card className="mb-6 shadow-lg">
            <CardHeader className="bg-gray-50">
              <CardTitle className="text-xl">Select Hearth Shape *</CardTitle>
              <p className="text-sm text-gray-600 mt-1">Choose the shape that best fits your fireplace</p>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {hearthShapes.map((shape) => (
                  <button
                    key={shape.id}
                    type="button"
                    onClick={() => {
                      setSelectedShape(shape);
                      // Handle piece count based on shape
                      if (shape.pieces === 2 && pieces.length === 1) {
                        addPiece();
                      } else if (shape.pieces === 1 && pieces.length > 1) {
                        // Remove extra pieces when switching to single piece shape
                        setPieces([pieces[0]]);
                      }
                      // Custom shape keeps existing pieces (can be 1+)
                    }}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedShape?.id === shape.id
                        ? 'border-gray-800 bg-gray-100 shadow-md'
                        : 'border-gray-300 hover:border-gray-500'
                    }`}
                  >
                    <div 
                      className="w-full h-24 mb-2"
                      dangerouslySetInnerHTML={{ __html: shape.svg }}
                    />
                    <h4 className="font-semibold text-sm mb-1">{shape.name}</h4>
                    <p className="text-xs text-gray-600">{shape.description}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Material Selection */}
          <Card className="mb-6 shadow-lg">
            <CardHeader className="bg-gray-50">
              <CardTitle className="text-xl">Select Material *</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid md:grid-cols-3 gap-4">
                {hearthMaterials.map((material) => (
                  <button
                    key={material.id}
                    type="button"
                    onClick={() => setSelectedMaterial(material)}
                    className={`rounded-lg border-2 transition-all overflow-hidden ${
                      selectedMaterial?.id === material.id
                        ? 'border-gray-800 bg-gray-100 shadow-md'
                        : 'border-gray-300 hover:border-gray-500'
                    }`}
                  >
                    {material.imageUrl ? (
                      <div className="relative">
                        <img
                          src={material.imageUrl}
                          alt={material.name}
                          className="w-full h-40 object-cover"
                        />
                        {selectedMaterial?.id === material.id && (
                          <div className="absolute top-2 right-2 bg-green-600 text-white rounded-full p-1">
                            <Check className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="h-40 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                        <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                    <div className="p-4 text-left">
                      <h4 className="font-semibold text-base">{material.name}</h4>
                      <p className="text-sm text-gray-600 mt-1">Finish: {material.finish}</p>
                    </div>
                  </button>
                ))}
              </div>

              {selectedMaterial?.id === 'custom' && (
                <div className="mt-4 p-4 bg-gray-50 border border-gray-300 rounded-lg">
                  <Label>Custom Material Name *</Label>
                  <Input
                    value={customMaterial}
                    onChange={(e) => setCustomMaterial(e.target.value)}
                    placeholder="e.g., Polished White Marble"
                    required
                    className="mt-2"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Dimensions */}
          {selectedShape && (
            <Card className="mb-6 shadow-lg">
              <CardHeader className="bg-gray-50">
                <CardTitle className="text-xl">Hearth Dimensions</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {pieces.map((piece, index) => (
                  <div key={index} className="border rounded-lg p-4 mb-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-700">{piece.name}</h4>
                      {((selectedShape.pieces === 2 || selectedShape.pieces === 'variable') && pieces.length > 1) && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removePiece(index)}
                          className="text-red-600"
                        >
                          Remove
                        </Button>
                      )}
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label>Width (left to right) *</Label>
                        <Input
                          type="number"
                          value={piece.width_mm}
                          onChange={(e) => updatePiece(index, 'width_mm', e.target.value)}
                          placeholder="e.g., 1200"
                          required
                        />
                      </div>
                      <div>
                        <Label>Depth (front to back) *</Label>
                        <Input
                          type="number"
                          value={piece.depth_mm}
                          onChange={(e) => updatePiece(index, 'depth_mm', e.target.value)}
                          placeholder="e.g., 500"
                          required
                        />
                      </div>
                      <div>
                        <Label>Thickness</Label>
                        <Input
                          type="text"
                          value="20mm"
                          disabled
                          className="bg-gray-100"
                        />
                        <p className="text-xs text-gray-600 mt-1">Standard thickness for hearths</p>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Add Piece Button for Custom Shapes */}
                {selectedShape.pieces === 'variable' && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addPiece}
                    className="w-full border-dashed border-2 border-gray-400 hover:border-gray-600"
                  >
                    + Add Another Piece
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* T-Shape Cut-Out Dimensions */}
          {selectedShape?.id === 't-shape-single' && (
            <Card className="mb-6 shadow-lg border-2 border-gray-800">
              <CardHeader className="bg-gray-800 text-white">
                <CardTitle className="text-xl">T-Shape Cut-Out Dimensions *</CardTitle>
                <p className="text-gray-300 text-sm mt-1">Dimensions of the cut-outs on left and right sides</p>
              </CardHeader>
              <CardContent className="p-6">
                <div className="mb-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="same_cutouts"
                      checked={sameCutouts}
                      onChange={(e) => setSameCutouts(e.target.checked)}
                      className="w-4 h-4 text-gray-800"
                    />
                    <Label htmlFor="same_cutouts" className="ml-2">Both cut-outs are the same size</Label>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Left Cut-Out */}
                  <div className="border rounded-lg p-4 bg-blue-50">
                    <h4 className="font-semibold text-gray-900 mb-3">Left Cut-Out *</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Width (left to right) *</Label>
                        <Input
                          type="number"
                          value={leftCutout.width}
                          onChange={(e) => setLeftCutout({ ...leftCutout, width: e.target.value })}
                          placeholder="e.g., 300"
                          required
                        />
                      </div>
                      <div>
                        <Label>Depth (front to back) *</Label>
                        <Input
                          type="number"
                          value={leftCutout.depth}
                          onChange={(e) => setLeftCutout({ ...leftCutout, depth: e.target.value })}
                          placeholder="e.g., 200"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right Cut-Out */}
                  {!sameCutouts && (
                    <div className="border rounded-lg p-4 bg-green-50">
                      <h4 className="font-semibold text-gray-900 mb-3">Right Cut-Out *</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Width (left to right) *</Label>
                          <Input
                            type="number"
                            value={rightCutout.width}
                            onChange={(e) => setRightCutout({ ...rightCutout, width: e.target.value })}
                            placeholder="e.g., 300"
                            required
                          />
                        </div>
                        <div>
                          <Label>Depth (front to back) *</Label>
                          <Input
                            type="number"
                            value={rightCutout.depth}
                            onChange={(e) => setRightCutout({ ...rightCutout, depth: e.target.value })}
                            placeholder="e.g., 200"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Slips Option */}
          <Card className="mb-6 shadow-lg">
            <CardHeader className="bg-gray-50">
              <CardTitle className="text-xl">Raised Hearth (Slips)</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="has_slips"
                  checked={hasSlips}
                  onChange={(e) => setHasSlips(e.target.checked)}
                  className="w-4 h-4 text-gray-800"
                />
                <Label htmlFor="has_slips" className="ml-2">I need slips to raise the hearth</Label>
              </div>

              {hasSlips && (
                <div className="space-y-4 pl-6">
                  <div>
                    <Label>Slip Height *</Label>
                    <select
                      value={slipHeight}
                      onChange={(e) => setSlipHeight(e.target.value)}
                      className="w-full md:w-64 px-3 py-2 border rounded-md"
                    >
                      <option value="40">40mm</option>
                      <option value="50">50mm (Standard)</option>
                      <option value="60">60mm</option>
                      <option value="custom">Custom Height</option>
                    </select>
                  </div>

                  {slipHeight === 'custom' && (
                    <div>
                      <Label>Custom Slip Height (mm) *</Label>
                      <Input
                        type="number"
                        value={customSlipHeight}
                        onChange={(e) => setCustomSlipHeight(e.target.value)}
                        placeholder="e.g., 55"
                        required
                      />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Delivery/Collection Option */}
          <Card className="mb-6 shadow-lg">
            <CardHeader className="bg-gray-50">
              <CardTitle className="text-xl">Delivery Method *</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setDeliveryOption('delivery')}
                  className={`p-6 rounded-lg border-2 transition-all text-left ${
                    deliveryOption === 'delivery'
                      ? 'border-gray-800 bg-gray-100 shadow-md'
                      : 'border-gray-300 hover:border-gray-500'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      <svg className="w-6 h-6 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-1">Delivery</h4>
                      <p className="text-sm text-gray-600">We'll deliver to your address</p>
                      {deliveryOption === 'delivery' && (
                        <p className="text-xs text-green-600 font-medium mt-2">✓ Selected</p>
                      )}
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setDeliveryOption('collection')}
                  className={`p-6 rounded-lg border-2 transition-all text-left ${
                    deliveryOption === 'collection'
                      ? 'border-gray-800 bg-gray-100 shadow-md'
                      : 'border-gray-300 hover:border-gray-500'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      <svg className="w-6 h-6 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-1">Collection</h4>
                      <p className="text-sm text-gray-600">I'll collect from your workshop</p>
                      {deliveryOption === 'collection' && (
                        <p className="text-xs text-green-600 font-medium mt-2">✓ Selected</p>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Additional Notes */}
          <Card className="mb-6 shadow-lg">
            <CardContent className="p-6">
              <Label className="text-base text-gray-700">Additional Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any special requirements or questions?"
                rows={4}
                className="mt-2"
              />
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gray-800 hover:bg-gray-900 text-lg py-6"
          >
            {loading ? 'Submitting...' : 'Get My Free Hearth Quote'}
          </Button>

          <p className="text-center text-sm text-gray-400 mt-4">
            By submitting this form, you agree to be contacted about your quote request
          </p>
        </form>
      </div>
    </div>
  );
}

export default PublicHearthQuote;
